import { useRef, useState } from 'react';
import { Box, Button, Stack, TextField, IconButton, CircularProgress } from '@mui/material';
import { UploadFile, Close, InsertDriveFile, Add } from '@mui/icons-material';
import { filesApi } from '../api/files.api';

export interface UploadedFile { url: string; name: string }

interface PendingFile { key: string; url: string | null; name: string; uploading: boolean; error?: boolean }

interface Props {
  /** Called once with every ready file when the user clicks "Add". */
  onAdd: (files: UploadedFile[]) => void;
  accept?: string;
  disabled?: boolean;
  label?: string;
}

/**
 * Batch document uploader. Lets the user pick several files at once (or keep adding more one
 * at a time), rename each before saving, and then commits the whole set via onAdd. Uploads to
 * POST /files/upload as each file is chosen; the name is editable and defaults to the original.
 */
export function MultiFileUpload({ onAdd, accept = '.pdf,image/*,.doc,.docx,.xls,.xlsx', disabled, label = 'Add files' }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState<PendingFile[]>([]);
  const seq = useRef(0);

  const pick = () => inputRef.current?.click();

  const onFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (inputRef.current) inputRef.current.value = '';
    if (!files.length) return;
    // Seed placeholder rows immediately so the UI shows progress, then upload each.
    const rows: PendingFile[] = files.map((f) => ({ key: `f${seq.current++}`, url: null, name: f.name, uploading: true }));
    setPending((prev) => [...prev, ...rows]);
    await Promise.all(files.map(async (file, i) => {
      const key = rows[i].key;
      try {
        const r = await filesApi.upload(file);
        setPending((prev) => prev.map((p) => (p.key === key ? { ...p, url: r.url, uploading: false } : p)));
      } catch {
        setPending((prev) => prev.map((p) => (p.key === key ? { ...p, uploading: false, error: true } : p)));
      }
    }));
  };

  const rename = (key: string, name: string) => setPending((prev) => prev.map((p) => (p.key === key ? { ...p, name } : p)));
  const drop = (key: string) => setPending((prev) => prev.filter((p) => p.key !== key));

  const ready = pending.filter((p) => p.url && !p.uploading && !p.error);
  const busy = pending.some((p) => p.uploading);

  const commit = () => {
    if (!ready.length) return;
    onAdd(ready.map((p) => ({ url: p.url as string, name: p.name.trim() || (p.url as string).split('/').pop() || 'file' })));
    setPending([]);
  };

  return (
    <Box>
      <input ref={inputRef} type="file" accept={accept} hidden multiple onChange={onFiles} />
      {pending.length === 0 ? (
        <Button size="small" variant="outlined" startIcon={<UploadFile />} onClick={pick} disabled={disabled}>{label}</Button>
      ) : (
        <Stack spacing={0.75} sx={{ mt: 0.5 }}>
          {pending.map((p) => (
            <Stack key={p.key} direction="row" spacing={0.5} alignItems="center">
              {p.uploading ? <CircularProgress size={14} /> : <InsertDriveFile fontSize="small" color={p.error ? 'error' : 'action'} />}
              <TextField size="small" variant="standard" value={p.name} onChange={(e) => rename(p.key, e.target.value)}
                placeholder="File name" disabled={p.uploading} error={p.error} sx={{ flex: 1, minWidth: 120 }}
                helperText={p.error ? 'Upload failed' : undefined} />
              <IconButton size="small" onClick={() => drop(p.key)} aria-label="remove"><Close sx={{ fontSize: 14 }} /></IconButton>
            </Stack>
          ))}
          <Stack direction="row" spacing={1}>
            <Button size="small" startIcon={<Add />} onClick={pick} disabled={disabled || busy}>Add more</Button>
            <Button size="small" variant="contained" onClick={commit} disabled={busy || ready.length === 0}>
              Add {ready.length || ''} {ready.length === 1 ? 'file' : 'files'}
            </Button>
          </Stack>
        </Stack>
      )}
    </Box>
  );
}
